<template>
  <div class="delivery-page">
    <!-- 顶部导航 -->
    <van-nav-bar
      :title="isReadOnly ? '物流信息' : '填写收货地址'"
      left-arrow
      @click-left="$router.go(-1)"
    />

    <!-- 加载中骨架 -->
    <div v-if="loading" class="loading-skeleton">
      <div class="skeleton-block"></div>
      <div class="skeleton-block"></div>
    </div>

    <!-- ==================== 已提交：只读详情 ==================== -->
    <template v-else-if="isReadOnly && deliveryInfo">
      <!-- 顶部状态卡 -->
      <div class="status-hero" :class="'hero-status-' + deliveryInfo.DeliveryStatus">
        <div class="hero-icon">
          <span v-if="deliveryInfo.DeliveryStatus === 2">📮</span>
          <span v-else-if="deliveryInfo.DeliveryStatus === 3">🚚</span>
          <span v-else-if="deliveryInfo.DeliveryStatus === 4">✅</span>
          <span v-else>📦</span>
        </div>
        <div class="hero-title">{{ deliveryInfo.DeliveryStatusName }}</div>
        <div class="hero-subtitle">
          <span v-if="deliveryInfo.DeliveryStatus === 2">您的地址已提交，管理员将尽快为您安排发货</span>
          <span v-else-if="deliveryInfo.DeliveryStatus === 3">证书已发出，请耐心等待签收</span>
          <span v-else-if="deliveryInfo.DeliveryStatus === 4">证书已签收，祝您学习顺利</span>
        </div>
      </div>

      <!-- 收件信息卡 -->
      <div class="info-card">
        <div class="info-card-header">
          <span class="info-card-title">📍 收件信息</span>
        </div>
        <div class="info-row">
          <span class="info-label">收件人</span>
          <span class="info-value">{{ deliveryInfo.ReceiverName }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">联系电话</span>
          <span class="info-value">{{ deliveryInfo.ReceiverPhone }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">收货地址</span>
          <span class="info-value">{{ deliveryInfo.FullAddress }}</span>
        </div>
      </div>

      <!-- 物流信息卡（仅有发货数据时显示） -->
      <div
        class="info-card"
        v-if="deliveryInfo.ExpressCompany || deliveryInfo.ExpressNo || deliveryInfo.ShipTime"
      >
        <div class="info-card-header">
          <span class="info-card-title">🚚 物流详情</span>
        </div>
        <div class="info-row" v-if="deliveryInfo.ExpressCompany">
          <span class="info-label">快递公司</span>
          <span class="info-value">{{ deliveryInfo.ExpressCompany }}</span>
        </div>
        <div class="info-row" v-if="deliveryInfo.ExpressNo">
          <span class="info-label">快递单号</span>
          <span class="info-value info-value-mono">{{ deliveryInfo.ExpressNo }}</span>
        </div>
        <div class="info-row" v-if="deliveryInfo.ShipTime">
          <span class="info-label">发货时间</span>
          <span class="info-value">{{ formatTime(deliveryInfo.ShipTime) }}</span>
        </div>
        <div class="info-row" v-if="deliveryInfo.ReceiveTime">
          <span class="info-label">签收时间</span>
          <span class="info-value">{{ formatTime(deliveryInfo.ReceiveTime) }}</span>
        </div>
      </div>

    </template>

    <!-- ==================== 未填写：可编辑表单 ==================== -->
    <template v-else>
      <!-- 顶部引导卡 -->
      <div class="status-hero hero-status-pending">
        <div class="hero-icon">📮</div>
        <div class="hero-title">填写收货地址</div>
        <div class="hero-subtitle">请认真填写您的收件信息，提交后由管理员为您安排发货</div>
      </div>

      <van-form @submit="onSubmit" class="address-form">
        <div class="form-card">
          <van-field
            v-model="form.ReceiverName"
            name="ReceiverName"
            label="收件人"
            placeholder="请输入收件人姓名"
            :rules="[{ required: true, message: '请输入收件人姓名' }]"
          />
          <van-field
            v-model="form.ReceiverPhone"
            name="ReceiverPhone"
            label="联系电话"
            type="tel"
            placeholder="请输入联系电话"
            :rules="[{ required: true, message: '请输入联系电话' }, { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号' }]"
          />
          <van-field
            v-model="areaText"
            is-link
            readonly
            name="area"
            label="所在地区"
            placeholder="请选择省市区"
            @click="showAreaPicker = true"
            :rules="[{ required: true, message: '请选择所在地区' }]"
          />
          <van-field
            v-model="form.Address"
            name="Address"
            label="详细地址"
            type="textarea"
            rows="2"
            placeholder="请输入详细地址（街道、门牌号等）"
            :rules="[{ required: true, message: '请输入详细地址' }]"
          />
        </div>

        <div class="submit-btn">
          <van-button
            round
            block
            color="linear-gradient(135deg, #ff6b5b 0%, #d51900 100%)"
            native-type="submit"
            :loading="submitting"
            loading-text="提交中..."
          >
            确认提交
          </van-button>
        </div>
      </van-form>

      <!-- 地区选择器 -->
      <van-popup v-model="showAreaPicker" position="bottom">
        <van-area
          :area-list="areaList"
          @confirm="onAreaConfirm"
          @cancel="showAreaPicker = false"
        />
      </van-popup>
    </template>
  </div>
</template>

<script>
import { areaList } from '@vant/area-data'
import {
  WeChatCertificateDelivery_SubmitAddress,
  WeChatCertificateDelivery_GetDetail
} from '../api/api'

export default {
  name: 'certificate_delivery',
  data () {
    return {
      memberCertificateId: null,
      form: {
        MemberCertificateId: 0,
        ReceiverName: '',
        ReceiverPhone: '',
        Province: '',
        City: '',
        Area: '',
        Address: ''
      },
      areaText: '',
      showAreaPicker: false,
      areaList: areaList,
      submitting: false,
      loading: true,
      deliveryInfo: null
    }
  },
  computed: {
    // 已经提交过地址（DeliveryStatus >= 2）就进入只读模式
    isReadOnly () {
      return !!(this.deliveryInfo && this.deliveryInfo.DeliveryStatus >= 2)
    }
  },
  mounted () {
    this.memberCertificateId = this.$route.query.id
    if (this.memberCertificateId) {
      this.form.MemberCertificateId = parseInt(this.memberCertificateId)
      this.loadDeliveryInfo()
    } else {
      this.loading = false
    }
  },
  methods: {
    async loadDeliveryInfo () {
      this.loading = true
      try {
        const { data } = await WeChatCertificateDelivery_GetDetail({
          memberCertificateId: this.memberCertificateId
        })
        if (data.success && data.response) {
          this.deliveryInfo = data.response
        } else {
          this.deliveryInfo = null
        }
      } catch (e) {
        console.error('加载物流信息失败', e)
        this.deliveryInfo = null
      } finally {
        this.loading = false
      }
    },
    onAreaConfirm (values) {
      this.form.Province = values[0].name
      this.form.City = values[1].name
      this.form.Area = values[2].name
      this.areaText = values.map(item => item.name).join(' ')
      this.showAreaPicker = false
    },
    async onSubmit () {
      if (this.submitting) return
      this.submitting = true
      try {
        const { data } = await WeChatCertificateDelivery_SubmitAddress(this.form)
        if (data.success) {
          this.$toast.success('地址提交成功')
          await this.loadDeliveryInfo()
        } else {
          this.$toast.fail(data.msg || '提交失败')
        }
      } catch (e) {
        this.$toast.fail('提交失败')
      } finally {
        this.submitting = false
      }
    },
    formatTime (time) {
      if (!time) return ''
      const date = new Date(time)
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
    }
  }
}
</script>

<style scoped>
.delivery-page {
  min-height: 100vh;
  background: linear-gradient(180deg, #fff8f6 0%, #f5f9fc 30%);
  -webkit-overflow-scrolling: touch;
  padding-bottom: 30px;
}

/* 加载骨架 */
.loading-skeleton {
  padding: 20px 16px;
}

.skeleton-block {
  height: 140px;
  border-radius: 16px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s infinite;
  margin-bottom: 16px;
}

@keyframes skeleton-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* 顶部状态卡 */
.status-hero {
  margin: 16px;
  padding: 28px 20px 24px;
  border-radius: 20px;
  text-align: center;
  color: #fff;
  position: relative;
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
}

.status-hero::before {
  content: '';
  position: absolute;
  top: -40%;
  right: -20%;
  width: 180px;
  height: 180px;
  background: rgba(255, 255, 255, 0.12);
  border-radius: 50%;
}

.status-hero::after {
  content: '';
  position: absolute;
  bottom: -30%;
  left: -10%;
  width: 130px;
  height: 130px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 50%;
}

.hero-status-pending {
  background: linear-gradient(135deg, #ff9a44 0%, #ff6b5b 100%);
}

.hero-status-2 {
  background: linear-gradient(135deg, #ff6b5b 0%, #d51900 100%);
}

.hero-status-3 {
  background: linear-gradient(135deg, #4a6cf7 0%, #1e6cff 100%);
}

.hero-status-4 {
  background: linear-gradient(135deg, #52c41a 0%, #389e0d 100%);
}

.hero-icon {
  font-size: 48px;
  line-height: 1;
  margin-bottom: 10px;
  position: relative;
  z-index: 1;
}

.hero-title {
  font-size: 20px;
  font-weight: 700;
  letter-spacing: 1px;
  margin-bottom: 6px;
  position: relative;
  z-index: 1;
}

.hero-subtitle {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.92);
  line-height: 1.5;
  padding: 0 12px;
  position: relative;
  z-index: 1;
}

/* 信息卡 */
.info-card {
  margin: 0 16px 16px;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}

.info-card-header {
  padding: 14px 16px 8px;
  border-bottom: 1px solid #f5f7fa;
}

.info-card-title {
  font-size: 14px;
  font-weight: 600;
  color: #1a2a3a;
}

.info-row {
  display: flex;
  align-items: flex-start;
  padding: 14px 16px;
  border-bottom: 1px solid #f5f7fa;
}

.info-row:last-child {
  border-bottom: none;
}

.info-label {
  flex-shrink: 0;
  width: 80px;
  font-size: 14px;
  color: #8a9aaa;
}

.info-value {
  flex: 1;
  font-size: 14px;
  color: #1a2a3a;
  font-weight: 500;
  word-break: break-all;
  text-align: right;
}

.info-value-mono {
  font-family: 'SF Mono', Menlo, Consolas, monospace;
  letter-spacing: 0.5px;
}

/* 表单卡 */
.address-form {
  padding: 0;
}

.form-card {
  margin: 0 16px;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}

.form-card >>> .van-cell {
  padding: 14px 16px;
}

.form-card >>> .van-cell::after {
  border-color: #f5f7fa;
}

.form-card >>> .van-field__label {
  width: 80px;
  color: #8a9aaa;
  font-size: 14px;
}

.form-card >>> .van-field__control {
  font-size: 14px;
  color: #1a2a3a;
}

.submit-btn {
  margin: 24px 16px 0;
}

.submit-btn >>> .van-button {
  height: 48px;
  font-size: 16px;
  font-weight: 600;
  box-shadow: 0 6px 16px rgba(213, 25, 0, 0.3);
}
</style>



// WEBPACK FOOTER //
// src/views/certificate_delivery.vue